import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextApiRequest,
  NextApiResponse,
  PreviewData,
} from "next";
import type { ParsedUrlQuery } from "querystring";
import type { ReactElement } from "react";

export interface PageProps {
  dehydratedState: unknown;
  session?: Session;
}

type BackgroundColor = "gray" | "white" | "purple" | "indigo" | "gray-v2";

export interface Page<P extends PageProps = PageProps> {
  (props: P): ReactElement;
  /**
   * You can change the background color of the page by setting this property.
   * You can also set the background color for each device size if needed.
   */
  backgroundColor?:
    | BackgroundColor
    | {
        root: BackgroundColor;
        phone?: BackgroundColor;
        tablet?: BackgroundColor;
        desktop?: BackgroundColor;
        laptop?: BackgroundColor;
      };
  /**
   * Hide the navbar. This is useful for embedding the app in a web view.
   */
  hideNavBar?: boolean;
  /**
   * Hide the navbar on mobile. Uses css media queries to control the visibility.
   */
  hideNavBarOnMobile?: boolean;
  /**
   * Opt-in to displaying OneTrust's consent banner if the user is not logged in.
   */
  shouldShowConsentDialog?: boolean;
  /**
   * if there is another sticky item under navbar, you can hide the navbar's shadow
   */
  hideNavbarShadowOnMobile?: boolean;
  hideNavbarShadowOnDesktop?: boolean;
}

export interface SessionUser {
  id: number;
  avatar: string | null;
  name: string | null;
  email: string | null;
}

export interface Session {
  expires: string;
  user: SessionUser;
  accessToken: string;
  deviceId?: string;
  deviceType?: string;
  isNewUser?: boolean;
  isGoogleAuth?: boolean;
  /**
   * Hide the navbar throughout the app for this session
   */
  hideNavBar?: string | boolean;
  /**
   * Ignore Next.js routing and send events on the native
   * channel for this session
   */
  deferRoutingToWebView?: string | boolean;
}

export type ServerSideContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
  D extends PreviewData = PreviewData
> = GetServerSidePropsContext<Q, D>;

export type GetServerSideProps<
  P extends PageProps = PageProps,
  Q extends ParsedUrlQuery = ParsedUrlQuery,
  D extends PreviewData = PreviewData
> = (ctx: ServerSideContext<Q, D>) => Promise<GetServerSidePropsResult<P>>;

export type ApiRequestWithSession = NextApiRequest & {
  session: Session;
};

export type ApiResponse = NextApiResponse;
export interface SSRError {
  message: string;
  name: string;
  stack: string;
}

export type DateTime = {
  // input type - value returned by external data source
  raw: string | number | Date;
  value: Date;
  formatted: string;
};

export type Size = "small" | "medium" | "large" | "extraLarge" | "custom";

export enum Countries {
  AUSTRALIA = "Australia",
  UNITED_STATES = "United States",
  UNITED_KINGDOM = "United Kingdom",
  CANADA = "Canada",
  OTHER = "Other",
}

export enum TimeZones {
  // United States
  AMERICA_ANCHORAGE = "America/Anchorage",
  AMERICA_CHICAGO = "America/Chicago",
  AMERICA_DENVER = "America/Denver",
  AMERICA_LOS_ANGELES = "America/Los_Angeles",
  AMERICA_NEW_YORK = "America/New_York",
  AMERICA_PHOENIX = "America/Phoenix",
  PACIFIC_HONOLULU = "Pacific/Honolulu",

  // Australia
  AUSTRALIA_ADELAIDE = "Australia/Adelaide",
  AUSTRALIA_BRISBANE = "Australia/Brisbane",
  AUSTRALIA_BROKEN_HILL = "Australia/Broken_Hill",
  AUSTRALIA_CURRIE = "Australia/Currie",
  AUSTRALIA_DARWIN = "Australia/Darwin",
  AUSTRALIA_EUCLA = "Australia/Eucla",
  AUSTRALIA_HOBART = "Australia/Hobart",
  AUSTRALIA_LINDEMAN = "Australia/Lindeman",
  AUSTRALIA_LORD_HOWE = "Australia/Lord_Howe",
  AUSTRALIA_MELBOURNE = "Australia/Melbourne",
  AUSTRALIA_PERTH = "Australia/Perth",
  AUSTRALIA_SYDNEY = "Australia/Sydney",
  ANTARCTICA_MACQUARIE = "Antarctica/Macquarie",
  PACIFIC_NORFOLK = "Pacific/Norfolk",

  // United Kingdom
  EUROPE_LONDON = "Europe/London",

  // Ontario, Canada
  AMERICA_TORONTO = "America/Toronto",
}

export interface PhoneNumber {
  raw: string;
  formatted: string;
}

export type Money = {
  // the underlying value in cents
  value: number;
  // the formatted value
  formatted: string;
  currency?: string;
};

export interface CloudinaryResponse {
  secure_url: string;
  public_id: string;
  width: number;
  height: number;
  url: string;
}

export enum InsuranceCardUploadStatus {
  CASH_ONLY = "cash_only",
  REQUESTED_INSURANCE_CARDS = "requested_insurance_cards",
  INSURANCE_CARD_UPLOADED = "insurance_card_uploaded",
  INSURANCE_REJECTED = "insurance_rejected",
  INSURANCE_VERIFIED = "insurance_verified",
  INSURANCE_FAIL_ELIG_NOT_ACTIVE = "elig_check_failed_upload_new_card",
  INSURANCE_REJECTED_MEDICAID_MEDICARE = "insurance_rejected_medicaid_medicare",
}

export enum SessionDurationInMinutes {
  THIRTY_MINUTES = 30,
  FORTY_FIVE_MINUTES = 45,
  ONE_HOUR = 60,
}
